
/**
 * -----------------------------------------------------------------------------
 * Popup - Send Text Message
 * -----------------------------------------------------------------------------
 * This popup is used for sending text message to the user.
 */

import { Formik, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useSendMessageMutation } from "../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import { AdminFormFieldInput, AdminFormFieldSubmit } from "../../components/admin/formFields";
import { sendTextFormInitialValues, TFormFieldNames, YupValidationSchema } from "./validation";
import { FC, useMemo } from "react";
import { TLegacyDriver } from "../../api/types/Driver";
import { APP_CONFIG } from "../../constants/constants";
import FTModal from "../../components/common/FTModal";
import FormActionButtons from "../../components/admin/formActionButtons";

type TModalSendTextProps = {
  assignedDriver: TLegacyDriver | undefined;
};

const ModalSendText: FC<TModalSendTextProps> = ({ assignedDriver }) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'mapFleet.sendSms' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();

  // user mutation
  const [sendMessageTrigger] = useSendMessageMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showSendText: false }));
  };
  const formik = useFormik({
        initialValues: sendTextFormInitialValues,
        validationSchema : YupValidationSchema ,
        onSubmit: (values, { setSubmitting }) => {
          // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
          if (!assignedDriver || !assignedDriver?.contactphone) {
            toast.error(t('driver_details_not_found'));
            setSubmitting(false);
            return;
          }
          sendMessageTrigger({
            phone: `${APP_CONFIG.COUNTRY_CODE}${assignedDriver?.contactphone}`,
            body: values.message
          })
            .unwrap()
            .then((data) => {
              if (!!data?.message) {
                dispatch(setModalsData({ ...modalsState, showSendText: false }));
                toast.success(t('toast.send_success'), { autoClose: 10000 });
              } else {
                toast.error(t('toast.send_failed'));
              }
            })
            .catch((error) => {
              const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.send_failed')];
              toast.error(errors?.join(' '));
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      })
      const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
      } = formik;
        const invalidFields =
          Object.keys(errors)
            .filter(key => !!errors[key as TFormFieldNames]);
        const isFormValid = invalidFields.length === 0 && dirty;
        const MemoizedFormActionButtons = useMemo(() => {
          return (
            <>
              <FormActionButtons
                buttons={[
                  {
                    label:t('send'),
                    variant:"primary",
                    disabled:!isFormValid || isSubmitting 
                  },
                  {
                    label:tMain('cancel'),
                    type:"button",
                    variant:"danger-transparent",
                    disabled:isSubmitting, /* || isLoading*/
                    onClick:()=>hideModal()
                  },
                ]}
              />
            </>
          );
        }, [
          isFormValid,
          isSubmitting,        
        ]); 

  if (modalsState.showSendText === false) return null;

  return (
    <FTModal
      visible={modalsState.showSendText}
      heading={t("heading")}
      onHide={hideModal}
      containerStyle="max-w-[calc(100vw-4rem)] w-[560px] !top-20 md:top-10"
    >
          <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("sub_heading")}
            </p>

            <AdminFormFieldInput
              label={t('enter_text')}
              type="text"
              id="message"
              name="message"
              placeholder={t('place_holder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.message}
              touched={touched.message}
              error={errors.message}
            />
            {MemoizedFormActionButtons}
            {/* <AdminFormFieldSubmit
              label={t('send')}
              variant="primary"
              disabled={!isFormValid || isSubmitting } // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting } // || isLoading
              onClick={hideModal}
            /> */}
          </form>   
    </FTModal>
  )
};
export default ModalSendText;